


























import Vue from 'vue'
import TopBar from '@/components/TopBar'
import ChromeMessage, { MIN_CHROME_VERSION } from '@/components/ChromeMessage'
import AuthManager from '@/components/AuthManager'
import HelpScout from '@/components/HelpScout'
import LegalUpdatePrompt from '@/components/LegalUpdatePrompt'
import UserProfileContainer from '@/containers/UserProfileContainer'
import moment from 'moment'
import fb from '@/firebase'
import './App.scss'
import { isMobile, isMobileOnly } from 'mobile-device-detect'
import { CallTriage, Cast, InvitedCaster } from '@/types/casts'
import { AbilityAction, AbilitySubject } from './abilitiesByRole'
import { Feature } from './featureFlags/featureFlags'

export default Vue.extend({
  name: 'App',
  computed: {
    showHelpscout (): boolean {
      if (this.cefEvent) return false
      if (this.isPreview) return false
      if (this.inCast) return false
      if (!this.loginComplete) return false
      if (this.isWidget) return false
      if (this.isMobileOnly) return false
      if (this.isSelfServe) return false
      if (this.iosFront) return false
      return true
    },
    isSelfServe (): boolean {
      return this.$hasFeature(Feature.SELF_SERVE)
    },
    currentCast (): Cast {
      return this.$store.direct.state.events.currentCast
    },
    currentUserId (): string {
      return this.$store.direct.state.user.id
    },
    userLoggedIn (): boolean {
      return this.$store.direct.state.user.userLoggedIn
    },
    loginComplete (): boolean {
      return this.$store.direct.state.user.loginComplete
    },
    cefEvent (): boolean {
      return this.$store.direct.state.events.cefEvent
    },
    iosFront (): boolean {
      return this.$store.direct.state.events.iosFront || this.$route.name === 'iosFront' || this.$route.name === 'iosWrapper'
    },
    isCefOrIos (): boolean {
      return this.cefEvent || this.iosFront
    },
    inCast (): boolean {
      return this.$store.direct.state.events.inCast
    },
    teamCheck (): boolean | null {
      return this.$store.direct.state.user.teamCheck
    },
    userHasNoTeams (): boolean {
      return this.$store.direct.state.user.userHasNoTeams
    },
    isMobile (): boolean {
      return isMobile // *any* mobile device, i.e. tablets and phones etc
    },
    isMobileOnly (): boolean {
      return isMobileOnly // mobile smartphones only
    },
    castName (): string {
      return this.currentCast !== null ? this.currentCast.name : ''
    },
    eventEndTime () : number | null {
      return (this.currentCast.hasOwnProperty('end_date')) ? this.currentCast.end_date.seconds : null
    },
    canSwitchTeams (): boolean {
      return this.$can(AbilityAction.Do, AbilitySubject.Ops)
    },
    showTeamMessage (): boolean {
      let status = false
      if (!this.isCefOrIos) {
        if (this.teamCheck === false && this.userLoggedIn) {
          status = true
        }
      }
      return status
    },
    isWidget (): boolean {
      return this.$route.name === 'chatwidget'
    },
    isPreview (): boolean {
      return this.$route.name === 'preview'
    },
    isLayoutEditor (): boolean {
      return this.$route.name === 'layouteditor'
    },
    currentVersion (): string {
      return this.$store.direct.state.team.team.version
    },
    isChrome (): boolean {
      if (this.isCefOrIos) return true  // Cef does not want checks
      const stringVersion = navigator?.appVersion.split('Chrome/')[1]?.split(' ')[0]?.split('.')?.[0]
      if (stringVersion === undefined) return false
      const chromeVersion = parseInt(stringVersion)
      // @ts-ignore
      return window.chrome && chromeVersion >= MIN_CHROME_VERSION
    },
    isAnonymous (): boolean {
      const profile = this.$store.direct.state.user.profile
      return !!profile?.anonymous
    },
    currentInvitedCaster (): InvitedCaster | null {
      return this.currentCast?.invited_casters?.[this.currentUserId] ?? null
    },
    showTopBar (): boolean {
      if (this.isCefOrIos || this.isPreview || this.isWidget) return false
      if (!this.loginComplete && !this.canSwitchTeams) return false
      // Prevent the "flashing" of the topbar when you leave a cast as an anonymous user
      if (this.isAnonymous && !this.inCast) return false
      // Prevent the "flashing" of the topbar when you start a session as an anonymous user (CC-3486)
      if (this.inCast && this.isAnonymous && !this.currentInvitedCaster) return false
      // If you're anonymous, only show the topbar if you need to be able to start casting
      const hideTopbarTriages = [CallTriage.Queued, CallTriage.Rejected]
      const caster = this.currentInvitedCaster
      if (this.inCast && caster !== null && hideTopbarTriages.includes(caster.triage)) return false
      return true
    }
  },
  data: () => ({
    fullScreen: false,
    addProfileToCastTeam: fb.functions.httpsCallable('addProfileToCastTeam')
  }),
  created () {
    if (this.redirectOldHostname()) return

    this.$router.afterEach((to) => {
      this.fullScreen = (to.name === 'liveEvent')
    })
  },
  watch: {
    currentVersion (newVal: string, oldVal: string|undefined) {
      // Only redirect if newVal is set and oldVal is either undefined or an empty string
      // This will prevent redirecting during an active cast.
      if (newVal && !oldVal) this.redirect(newVal)
    },
    currentUserId (newVal: string) {
      if (newVal) this.addCurrentUserToCastTeam()
    }
  },
  methods: {
    extendEvent (): void {
      let endDate = moment.unix(this.currentCast.end_date.seconds)
      endDate = endDate.add(30, 'minutes')
      this.$store.direct.dispatch.events.updateEvent({
        id: this.currentCast.event_id,
        // @ts-ignore
        end_date: endDate.toDate(),
        // @ts-ignore
        start_date: moment.unix(this.currentCast.start_date.seconds).toDate(),
        team_id: this.currentCast.team_id
      })
    },
    async addCurrentUserToCastTeam (): Promise<void> {
      // If this.currentCast.id is set we're already part of that team. We cannot fetch the cast doc
      // of a team we're not part of. So skip if this.currentCast.id is set
      if (!this.userLoggedIn || this.currentCast.id) return
      const url = new URL(window.location.href)
      const token = url.searchParams.get('token')
      const castId = url.pathname.split('/event/')[1]
      if (!token || !castId) return
      const result = await this.addProfileToCastTeam({ user_id: this.currentUserId, cast_id: castId, token: token })
      if (result.data.success) window.location.reload()
      else this.$store.direct.dispatch.logs.addLog({
        message: `Unable to add user ${ this.currentUserId } to team of cast ${ castId }`,
        level: 'info',
        data: { ...result.data, userId: this.currentUserId, castId: castId, token: token }
      })
    },
    redirect (versionTeam: string): void {
      if (this.isCefOrIos) return
      if (1 === 1) return

      const url = new URL(window.location.href)
      const path = url.pathname.split('/')
      const versionUrl = path[1] // [0] is an empty string

      if (versionTeam !== versionUrl && url.hostname !== 'localhost') {
        path[1] = versionTeam
        url.pathname = path.join('/')
        window.location.href = url.href
        return
      }

      let route = url.searchParams.get('redirect')
      if (!route) return

      route = route.replace('%2F', '/')
      this.$router.push(route)
    },
    redirectOldHostname (): boolean {
      const oldHostRegex = /^cloudcast/i
      const url = new URL(window.location.href)
      if (oldHostRegex.test(url.hostname)) {
        url.hostname = url.hostname.replace(oldHostRegex, 'studio')
        window.location.href = url.href
        return true
      }
      return false
    }
  },
  components: {
    TopBar,
    ChromeMessage,
    AuthManager,
    UserProfileContainer,
    HelpScout,
    LegalUpdatePrompt
  }
})
